<!-- Header component that shows version number, logged in users 
    and dropdown for user actions -->
<template>
  <div class="header white py1 px2" v-bind:class="{ admin: profile.admin }">
    <div class="flex justify-between items-center">
      <div class="flex items-baseline">
        <h1 class="m0">
          <router-link to="/">LemmikkiHelppi</router-link>
        </h1>
        <span class="h6 inline-block ml1">Versio {{version}}</span>
        <span class="h4 inline-block ml1" v-if="profile.admin">YLLÄPITONÄKYMÄ</span>
      </div>
      <router-link to="/admin" v-if="profile.admin" class="h5 white admin-button">
        <span @click="changeToUserManager">
          <span class="slab mr1">Kirjautuneita eläinlääkäreitä</span>
          <div class="circle badge h6 semibold line-height-1">{{connectedUsers}}</div>
        </span>
      </router-link>
      <div v-else class="h5">
        <span class="slab mr1">Kirjautuneita eläinlääkäreitä</span>
        <div class="circle badge h6 semibold line-height-1">{{connectedUsers}}</div>
      </div>

      <div class="relative">
        <div class="cursor-pointer flex items-center" @click="showDropdown=!showDropdown">
          <div class="flex items-end flex-column line-height-1">
            <div class="user-label">Kirjautunut</div>
            <div class="user-name">{{profile.firstName}} {{profile.lastName}}</div>
          </div>
          <div v-if="profile.image" class="avatar circle mx1" v-bind:style="{ backgroundImage: 'url(' + profileImage(profile.image) + ')'}"></div>
          <div v-else class="avatar circle bg-gray mx1"></div>
          <div class="arrow">
            <svg width="10" height="6" viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 1L5 5 1 1" stroke="#fff" stroke-width="2" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
        <div v-if="showDropdown && connected">
          <ul class="dropdown-menu absolute list-reset h5" v-click-outside="onClickOutside">
            <li class="border-bottom border-gray-4" @click="openOwnProfile">
              <router-link to="/new" class="flex items-center p2">
                <div class="icon">
                  <svg width="24px" height="23px" viewBox="0 0 24 23" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                      <g id="Lemmikkihelppi-lääkäri" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round">
                          <g id="LemmikkiHelppi-Do--ui-admin-käyttäjähallinta" transform="translate(-1194.000000, -58.000000)" stroke="#00A1D4">
                              <g id="Group" transform="translate(1194.500000, 58.499577)">
                                  <polyline id="Stroke-798" points="0 13.0004234 6 13.0004234 7.5 11.0004234 9.5 13.9994234 12 8.99942335 14 16.0004234 15.5 13.0004234 23 13.0004234"></polyline>
                                  <path d="M20.7549,10.9994234 C21.5149,9.39442335 21.9999,7.72442335 21.9999,6.07442335 C21.9999,-1.61857665 12.6839,-2.21057665 11.4999,5.48142335 C10.3169,-2.21057665 0.9999,-1.61857665 0.9999,6.66542335 C0.9999,8.16542335 1.3479,9.62042335 1.9109,10.9994234" id="Stroke-799"></path>
                                  <path d="M4.2305,14.9994234 C7.3165,19.1244234 11.5005,21.8934234 11.5005,21.8934234 C11.5005,21.8934234 15.2565,18.9834234 18.2765,14.9994234" id="Stroke-800"></path>
                              </g>
                          </g>
                      </g>
                  </svg>
                </div>
                <div class="flex-auto">Potilasjono</div>
              </router-link>
            </li>
            <li class="border-bottom border-gray-4" @click="openOwnProfile">
              <router-link to="/profile" class="flex items-center p2">
                <div class="icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <g stroke="#00A1D4" fill="none" fill-rule="evenodd" stroke-linejoin="round">
                      <path d="M10 15s-1.5-.5-1.5-3c-.826 0-.826-2 0-2 0-.335-1.5-4 1-3.5.5-2 6-2 6.5 0 .347 1.388-.5 3.254-.5 3.5.826 0 .826 2 0 2 0 2.5-1.5 3-1.5 3v2.5c2.477.929 4.93 1.697 6.186 2.575A11.464 11.464 0 0 0 23.5 12C23.5 5.648 18.352.5 12 .5S.5 5.648.5 12c0 3.153 1.27 6.009 3.325 8.087 1.33-.92 3.952-1.746 6.175-2.587V15z"/>
                      <path d="M3.825 20.087A11.465 11.465 0 0 0 12 23.5c3.203 0 6.1-1.31 8.185-3.425"/>
                    </g>
                  </svg>
                </div>
                <div class="flex-auto">Omat tiedot</div>
              </router-link>
            </li>
            <li class="border-bottom border-gray-4">
              <router-link to="/phrases" class="flex items-center p2">
                <div class="icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <g stroke="#00A1D4" fill="none" fill-rule="evenodd" stroke-linejoin="round">
                      <path d="M23.5 10.759c0 5.18-5.148 9.383-11.5 9.383-1.418 0-2.775-.21-4.029-.593l-6.609 3.076 2.588-5.166C1.821 15.756.5 13.383.5 10.759.5 5.577 5.648 1.376 12 1.376s11.5 4.2 11.5 9.383z"/>
                      <path d="M7.5 8.5H14M7.5 11.5H17M7.5 14.5H17" stroke-linecap="round"/>
                    </g>
                  </svg>
                </div>
                <div class="flex-auto">Keskustelufraasit</div>
              </router-link>
            </li>
            <li @click="changeToUserManager" class="border-bottom border-gray-4" v-if="profile.admin">
              <router-link to="/admin" class="cursor-pointer flex items-center p2">
                <div class="icon">
                  <svg width="24" height="21" viewBox="0 0 24 21" xmlns="http://www.w3.org/2000/svg">
                    <g stroke="#00A1D4" fill="none" fill-rule="evenodd" stroke-linejoin="round">
                      <path d="M9.497 20.5H18.5s0-3.352-.474-4.781c-.413-1.243-3.396-2.075-6.526-3.255v-2.5s1-.619 1-3c1 0 1-2 0-2 0-.234 1.078-1.678.75-3C12.776.06 7.1.06 6.625 1.964c-2.369-.476-1.125 2.681-1.125 3v2c0 2.381 2 3 2 3v2.5C4.722 13.52 1.386 14.476.974 15.719.5 17.148.5 20.5.5 20.5h8.997z"/>
                      <path d="M21 20.5h2.5s0-3.352-.474-4.781c-.413-1.243-3.396-2.575-6.526-3.755v-1.5s1-.619 1-3c1 0 1-2 0-2 0-.234 1.078-1.862.75-3.184-.222-.89-2.125-2.298-3.625-1.298" stroke-linecap="round"/>
                    </g>
                  </svg>
                </div>
                <div class="flex-auto">Käyttäjähallinta</div>
              </router-link>
            </li>
            <li class="border-bottom border-gray-4" v-if="profile.iam.superUser && !profile.admin">
              <a class="cursor-pointer flex items-center p2" @click="changeToAdmin">
                <div class="icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.254 13.5H23.5v-3h-3.247c-.21-.867-.551-2.181-1.003-2.922l2.296-2.295-2.83-2.829L16.42 4.75c-.74-.452-2.053-.793-2.92-1.002V.5h-3v3.248c-.867.209-2.181.55-2.921 1.002L5.282 2.454l-2.83 2.829L4.75 7.579c-.452.74-.794 2.054-1.004 2.921H.5v3h3.246c.21.867.552 2.181 1.004 2.921l-2.297 2.297 2.829 2.828 2.296-2.297c.74.453 2.055.795 2.922 1.005V23.5h3v-3.246c.867-.21 2.18-.552 2.92-1.004l2.298 2.296 2.828-2.828-2.296-2.297c.452-.74.794-2.054 1.004-2.921zM16.5 12a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0z" stroke="#00A1D4" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="flex-auto">Ylläpito</div>
              </a>
            </li>
            <li class="border-bottom border-gray-4" v-if="profile.admin">
              <a class="cursor-pointer flex items-center p2" @click="changeToNormalUser">
                <div class="icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.254 13.5H23.5v-3h-3.247c-.21-.867-.551-2.181-1.003-2.922l2.296-2.295-2.83-2.829L16.42 4.75c-.74-.452-2.053-.793-2.92-1.002V.5h-3v3.248c-.867.209-2.181.55-2.921 1.002L5.282 2.454l-2.83 2.829L4.75 7.579c-.452.74-.794 2.054-1.004 2.921H.5v3h3.246c.21.867.552 2.181 1.004 2.921l-2.297 2.297 2.829 2.828 2.296-2.297c.74.453 2.055.795 2.922 1.005V23.5h3v-3.246c.867-.21 2.18-.552 2.92-1.004l2.298 2.296 2.828-2.828-2.296-2.297c.452-.74.794-2.054 1.004-2.921zM16.5 12a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0z" stroke="#00A1D4" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="flex-auto">Poistu ylläpidosta</div>
              </a>
            </li>
            <li>
              <a class="cursor-pointer flex items-center p2" @click="logout">
                <div class="icon">
                  <svg width="16" height="24" viewBox="0 0 16 24" xmlns="http://www.w3.org/2000/svg">
                    <g stroke="var(--red)" fill="none" fill-rule="evenodd" stroke-linejoin="round">
                      <path d="M15.5 16A7.5 7.5 0 0 1 8 23.5 7.5 7.5 0 0 1 .5 16 7.5 7.5 0 0 1 8 8.5a7.5 7.5 0 0 1 7.5 7.5z"/>
                      <path d="M8.5 16a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0z"/>
                      <path d="M8 16.5v3" stroke-linecap="round"/>
                      <path d="M13.5 10.907V5.5C13 2.74 10.762.5 8 .5s-5 2.24-5.5 5v5.407"/>
                    </g>
                  </svg>
                </div>
                <div class="flex-auto red">Kirjaudu ulos</div>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import vClickOutside from 'v-click-outside';
import Util from '@/util';
import router from '@/router';

export default {
  name: 'Heading',
  computed: {
    ...mapGetters({
      profile: 'profile',
      connectedUsers: 'connectedUsers',
      connected: 'connected',
    }),
  },
  data() {
    return {
      version: process.env.VUE_APP_VERSION,
      showDropdown: false,
    }
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  methods: {
    profileImage(image) {
      return Util.getProfileImageURL(image);
    },
    onClickOutside(event) {
      this.showDropdown = false;
    },
    logout() {
      this.$store.dispatch('logout');
    },
    changeToAdmin() {
      this.$store.dispatch('changeToAdmin');
    },
    changeToNormalUser() {
      this.$store.dispatch('changeToNormalUser');
      this.$store.commit('RESET_CHATS');
      router.push('/');
    },
    changeToUserManager() {
      this.$store.commit('SELECTED_PROFILE_LOADED', {})
    },
    openOwnProfile() {
      this.$store.commit('SELECTED_PROFILE_LOADED', this.profile)
    }
  }
};
</script>

<style scoped>
  .header {
    background-color: var(--blue);
  }

  .header.admin {
    background-color: var(--dark-blue);
  }

  h1 {
    font-size: 18px;
  }

  h1 a {
    color: var(--white);
    text-decoration: none;
  }

  .user-label {
    font-size: .5rem;
  }

  .user-name {
    font-family: "lahitapiolaslabbook", sans-serif;
    font-weight: normal;
  }

  .avatar {
    width: 2rem;
    height: 2rem;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .dropdown-menu {
    position: absolute;
    top: 100%;
    right: calc(var(--space-2) * -1);
    min-width: 280px;
    margin-top: var(--space-1);
    background-color: var(--white);
    color: var(--dark-blue);
    box-shadow: 0 2px 4px rgba(0,0,0,.125);
  }

  .dropdown-menu a {
    color: var(--blue);
    text-decoration: none;
  }

  .dropdown-menu a:hover {
    background-color: var(--gray-6);
  }

  .icon {
    display: flex;
    justify-content: center;
    flex: none;
    width: 24px;
    margin-right: var(--space-1);
  }

  .badge {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    background-color: var(--dark-blue);
  }

  .admin .badge {
    background-color: var(--blue);
  }

  .admin-button {
    text-decoration: none;
    background-color: var(--blue);
    border-radius: 99px;
    padding: 4px 4px 4px 16px;
  }

  .admin-button .badge {
    background-color: var(--dark-blue)
  }

</style>
